import { useMemo, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Button,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { useAuth } from "@/features/authentication";
import { formatMoney, formatNumber } from "@/features/shared";
import { ExpenseDialog, UpstreamTransactionLink, useSnackbar } from "@/features/ui";

import { PAYMENT_LINE_ACCOUNT_TYPES } from "../other-payment-form-constants";
import {
  PaymentSourceDialog,
  usePaymentSourceTypes,
} from "../payment-source-dialog";

export const OtherPaymentDetailsSection = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { outlet } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { sourceTypes } = usePaymentSourceTypes(outlet?.id);

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "otherPaymentDetails",
  });

  const otherPaymentDetails = useWatch({
    control,
    name: "otherPaymentDetails",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [sourceMenuAnchor, setSourceMenuAnchor] = useState(null);
  const [activeSource, setActiveSource] = useState(null);

  const total = useMemo(
    () =>
      (otherPaymentDetails || []).reduce(
        (sum, line) => sum + (Number(line.amount) || 0),
        0
      ),
    [otherPaymentDetails]
  );

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (data) => {
    if (editingIndex !== null) {
      // Merge so a fetched line keeps its source link when only the amount or
      // the note was edited.
      update(editingIndex, { ...fields[editingIndex], ...data });
    } else {
      append(data);
    }
  };

  const handleSourceLinesFetched = (lines) => {
    const alreadyLinked = new Set(
      (otherPaymentDetails || [])
        .filter((line) => line.sourceableId)
        .map((line) => `${line.sourceableType}:${line.sourceableId}`)
    );

    const fresh = lines.filter(
      (line) =>
        !alreadyLinked.has(`${line.sourceableType}:${line.sourceableId}`)
    );

    if (fresh.length < lines.length) {
      showSnackbar(
        "Sebagian dokumen sudah ada pada pembayaran ini dan dilewati",
        "warning"
      );
    }

    fresh.forEach((line) => append(line));
  };

  const columns = [
    {
      field: "code",
      headerName: "Akun",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => row.outletChartOfAccount?.code || "-",
    },
    {
      field: "name",
      headerName: "Nama Akun",
      flex: 2,
      sortable: false,
      valueGetter: (_, row) => row.outletChartOfAccount?.name || "-",
    },
    {
      field: "amount",
      headerName: "Nilai (Rp)",
      flex: 1.5,
      type: "number",
      sortable: false,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "sourceReferenceNumber",
      headerName: "Sumber",
      flex: 1.5,
      sortable: false,
      renderCell: ({ value }) =>
        value ? (
          <UpstreamTransactionLink
            referenceNumber={value}
            shortReference={false}
          />
        ) : (
          "-"
        ),
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
      sortable: false,
      valueGetter: (note) => note || "-",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Aksi",
      getActions: (params) => {
        const index = fields.findIndex((field) => field.id === params.id);
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Ubah"
            onClick={() => handleEdit(index)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Hapus"
            onClick={() => remove(index)}
            color="error"
          />,
        ];
      },
    },
  ];

  const editingLine = editingIndex !== null ? fields[editingIndex] : null;

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6">Rincian Pembayaran</Typography>
          <Stack direction="row" spacing={1}>
            {sourceTypes.length > 0 && (
              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                onClick={(event) => setSourceMenuAnchor(event.currentTarget)}
                disabled={!outlet?.id}
              >
                Ambil
              </Button>
            )}
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={handleAdd}
              disabled={!outlet?.id}
            >
              Tambah Baris
            </Button>
          </Stack>
        </Stack>

        <Menu
          anchorEl={sourceMenuAnchor}
          open={Boolean(sourceMenuAnchor)}
          onClose={() => setSourceMenuAnchor(null)}
        >
          {sourceTypes.map((source) => (
            <MenuItem
              key={source.type}
              onClick={() => {
                setActiveSource(source);
                setSourceMenuAnchor(null);
              }}
            >
              {source.label}
            </MenuItem>
          ))}
        </Menu>

        <DataGrid
          rows={fields}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          hideFooter
        />

        {errors.otherPaymentDetails?.message && (
          <Typography variant="body2" color="error">
            {errors.otherPaymentDetails.message}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={{ justifyContent: "flex-end", alignItems: "baseline" }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Nilai
          </Typography>
          <Typography variant="h6">{formatMoney(total)}</Typography>
        </Stack>

        {dialogOpen && (
          <ExpenseDialog
            outletId={outlet?.id}
            title={editingIndex !== null ? "Ubah Baris" : "Tambah Baris"}
            amountLabel="Nilai"
            allowedAccountTypes={PAYMENT_LINE_ACCOUNT_TYPES}
            requirePositiveAmount
            // A fetched line settles a specific document, so its account is the
            // source's — the backend rejects a payment that changes it.
            accountDisabled={Boolean(editingLine?.sourceableId)}
            onClose={() => setDialogOpen(false)}
            onSubmit={handleSave}
            defaultValues={editingLine}
          />
        )}

        {activeSource && (
          <PaymentSourceDialog
            source={activeSource}
            outletId={outlet?.id}
            onClose={() => setActiveSource(null)}
            onSubmit={handleSourceLinesFetched}
          />
        )}
      </Stack>
    </Paper>
  );
};
